import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  intlShape
} from 'react-intl';
import { DateTime } from 'luxon';
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Container,
  Alert
} from 'reactstrap';
import { withRouter } from 'react-router';
import RouterPropTypes from 'react-router-prop-types';
import { get, sortBy } from 'lodash';

import RouteService from '../../../services/RouteService';
import HelperNavigation from '../../common/HelperNavigation';
import globalTranslations from '../../../i18n/globalTranslations';
import Paths from '../../../types/Paths';
import { modelOf } from '../../../prop-types';
import ContentForState from '../../loader/ContentForState';
import SEOTitle from '../../head/SEOTitle';
import PageTitle from '../../common/PageTitle';
import CountryStore from '../../../store/CountryStore';
import ProductImage from '../../product/ProductImage';
import ProductImageModel from '../../../models/ProductImage';
import UIStore from '../../../store/UIStore';
import OrderStatusLabel from '../../order/OrderStatusLabel';
import OrderTotalClass from '../../../types/OrderTotalClass';
import Price from '../../product/Price';
import ProductWarranty from '../../product/ProductWarranty';
import UnexpectedError from '../../loader/UnexpectedError';
import MaybeExternalLink from '../../common/MaybeExternalLink';
import ConfigStore from '../../../store/ConfigStore';
import ProductTypeClass from '../../../types/ProductTypeClass';
import AccountStore from '../../../store/AccountStore';
import RecurringOrderStore from '../../../store/RecurringOrderStore';
import MainRecurringOrder from '../../order/MainRecurringOrder';
import RequestState from '../../../types/RequestState';
import RecurringOrderProductModify from '../../order/RecurringOrderProductModify';

const messages = defineMessages({
  'myAccount.orderTitle': {
    id: 'myAccount.orderTitle',
    defaultMessage: 'Order {orderId}'
  }
});

const productTotals = [OrderTotalClass.TOTAL, OrderTotalClass.EXTRA_TOTAL];

const OrderHeaders = {
  image: <FormattedMessage {...globalTranslations.productTitle} />,
  name: <FormattedMessage id="order.productNameTitle" defaultMessage="Name" />,
  quantity: <FormattedMessage {...globalTranslations.quantityTitle} />,
  deliveredQuantity: (
    <FormattedMessage
      id="order.deliveredQuantityTitle"
      defaultMessage="Delivered qty"
    />
  ),
  unitPrice: (
    <FormattedMessage id="global.unitPriceTitle" defaultMessage="Unit price" />
  ),
  totalSum: (
    <FormattedMessage id="order.totalShortTitle" defaultMessage="Total" />
  )
};

@observer
export class MyAccountRecurringOrder extends Component {
  state = {
    updateError: null
  };

  componentDidMount() {
    this.maybeLoadOrder();
  }

  getCurrentOrderId = () => {
    return this.props.match.params.id;
  };

  maybeLoadOrder = () => {
    const { recurringOrderStore } = this.props;

    if (
      !recurringOrderStore.orders.get(this.getCurrentOrderId()) &&
      !recurringOrderStore.orderStates.get(this.getCurrentOrderId())
    ) {
      recurringOrderStore.loadOrder(this.getCurrentOrderId());
    }
  };

  navigateToOrderList() {
    const { history, routeService } = this.props;
    history.push(routeService.getPath(Paths.MyAccountRecurringOrderList));
  }

  onSuccess = () => {
    const { recurringOrderStore } = this.props;
    this.setState({ updateError: null });
    recurringOrderStore.loadOrder(this.getCurrentOrderId());
  };

  onError = (error) => {
    this.setState({
      updateError: error,
      requestId: this.state.requestId + 1
    });
  };

  onOrderUpdate = (order) => {
    const { recurringOrderStore } = this.props;

    recurringOrderStore
      .updateOrder(this.getCurrentOrderId(), order)
      .then(() => {
        this.onSuccess();
      })
      .catch((error) => {
        this.onError(error);
      });
  };

  onProductUpdate = (updateProduct) => {
    const { recurringOrderStore } = this.props;

    recurringOrderStore
      .updateProduct(this.getCurrentOrderId(), updateProduct)
      .then(() => {
        this.onSuccess();
      })
      .catch((error) => {
        this.onError(error);
      });
  };

  onProductRemove = (id) => {
    const { recurringOrderStore } = this.props;

    recurringOrderStore
      .removeProduct(this.getCurrentOrderId(), { extendedId: id })
      .then(() => {
        recurringOrderStore.loadOrder(this.getCurrentOrderId());
        this.setState({ removeError: null });
      })
      .catch((error) =>
        this.setState({
          removeError: error,
          requestId: this.state.requestId + 1
        })
      );
  };

  getOrderState = () => {
    const { recurringOrderStore } = this.props;
    return recurringOrderStore.orderStates.get(this.getCurrentOrderId());
  };

  getOrder = () => {
    const { recurringOrderStore } = this.props;

    return recurringOrderStore.orders.get(this.getCurrentOrderId());
  };

  getBreadcrumbsPath = () => {
    const { intl, routeService } = this.props;
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.myAccount),
        url: Paths.MyAccount
      },
      {
        text: intl.formatMessage(globalTranslations.recurringOrdersTitle),
        url: Paths.MyAccountRecurringOrderList
      },
      {
        text: intl.formatMessage(messages['myAccount.orderTitle'], {
          orderId: this.getCurrentOrderId()
        }),
        url: Paths.MyAccountOrderList
      }
    ]);
  };

  createProductImage = (url, id) => {
    return url
      ? ProductImageModel.create({
          id: 0,
          product_id: id,
          sizes: {
            small: url
          }
        })
      : null;
  };

  renderProductHeaderCol = (label, sizes) => {
    const actualSizes = Object.assign(
      {
        xs: 4
      },
      sizes
    );
    return (
      <Col className="MyAccountOrder__product-header" {...actualSizes}>
        {label}
      </Col>
    );
  };

  renderProductHeaderRow = () => {
    return (
      <Row className="MyAccountOrder__product-headers">
        {this.renderProductHeaderCol(OrderHeaders.image, { md: 2, xl: 1 })}
        {this.renderProductHeaderCol(OrderHeaders.name, { md: 6, xl: 7 })}
        {this.renderProductHeaderCol(OrderHeaders.quantity, { md: 1 })}
        {this.renderProductHeaderCol(OrderHeaders.unitPrice, { md: 2 })}
        {this.renderProductHeaderCol(OrderHeaders.totalSum, { md: 1 })}
      </Row>
    );
  };

  renderProductLink = (product) => {
    return product.isPhysicalProduct && product.product_url ? (
      <MaybeExternalLink to={product.product_url}>
        {product.name}
      </MaybeExternalLink>
    ) : (
      product.name
    );
  };

  renderProductCode = (product) => {
    const { configStore, accountStore } = this.props;
    if (configStore.productCard.showProductCode || accountStore.isRetailer) {
      const collectionOrMultiProduct = product.id.includes('{');
      const index = collectionOrMultiProduct
        ? product.id.indexOf('{')
        : product.id;
      return product.id.substring(0, index);
    }
  };

  renderProductRow = (product) => {
    const { isMobile } = this.props.uiStore;

    const emptyColumn = !isMobile ? <Col md="3" /> : null;
    const order = this.getOrder();

    return (
      <Row key={product.id}>
        {!isMobile && (
          <Col className="MyAccountOrder__products-image" xs="2" xl="1">
            {product.isPhysicalProduct && (
              <MaybeExternalLink to={product.product_url}>
                <ProductImage
                  product={product}
                  productImage={this.createProductImage(
                    product.image_url,
                    product.id
                  )}
                  size="small"
                  forceAspectRatio={false}
                />
              </MaybeExternalLink>
            )}
          </Col>
        )}
        {isMobile && this.renderProductHeaderCol(OrderHeaders.name)}
        <Col xs="8" md="6" xl="7">
          <div className={'MyAccountOrder__products-name-wrapper'}>
            {this.renderProductLink(product)}
            {product &&
              product.type !== ProductTypeClass.SHIPPING_COST &&
              this.renderProductCode(product)}
          </div>
          {product.variations &&
            product.variations.length > 0 && (
              <ul>
                {product.variations.map((variation, index) => (
                  <li key={index}>{variation}</li>
                ))}
              </ul>
            )}
          {product.reference && (
            <div className="MyAccountOrder__products-reference">
              <FormattedMessage {...globalTranslations.yourReferenceTitle} />
              {': ' + product.reference}
            </div>
          )}
          {product.warranty_in_months > 0 && (
            <div className="MyAccountOrder__products-warranty">
              <ProductWarranty warranty={product.warranty_in_months} />
            </div>
          )}
        </Col>
        {product.isPhysicalProduct ? (
          <Fragment>
            {isMobile && this.renderProductHeaderCol(OrderHeaders.quantity)}
            <Col xs="8" md="1">
              {product.quantity_ordered} {product.stock_unit}
            </Col>
            {isMobile && this.renderProductHeaderCol(OrderHeaders.unitPrice)}
            <Col xs="8" md="2">
              <div
                dangerouslySetInnerHTML={{ __html: product.final_unit_price }}
              />
              {product.final_unit_price !== product.starting_unit_price && (
                <div
                  style={{ textDecoration: 'line-through' }}
                  dangerouslySetInnerHTML={{
                    __html: product.starting_unit_price
                  }}
                />
              )}
            </Col>
          </Fragment>
        ) : (
          emptyColumn
        )}
        {isMobile && this.renderProductHeaderCol(OrderHeaders.totalSum)}
        <Col
          xs="8"
          md="1"
          dangerouslySetInnerHTML={{ __html: product.total_price }}
        />
        <Col>
          <RecurringOrderProductModify
            onUpdate={this.onProductUpdate}
            onRemove={this.onProductRemove}
            cycles={order.cycles}
            minCustomDays={order.min_custom_cycle}
            product={product}
          />
        </Col>
      </Row>
    );
  };

  renderTotals = (visibleTotalClasses) => {
    const order = this.getOrder();
    const filtered = order.totals.filter((total) => {
      return visibleTotalClasses.includes(total.class);
    });

    return (
      <div className="MyAccountOrder__totals">
        {sortBy(filtered, 'value').map((totalsItem) => (
          <Row key={totalsItem.name}>
            <Col xs="8" className="MyAccountOrder__total-item">
              {totalsItem.name}
            </Col>
            <Col xs="4" className="MyAccountOrder__total-value">
              <Price price={totalsItem.value} />
            </Col>
          </Row>
        ))}
      </div>
    );
  };

  renderError = () => {
    const { updateError, requestId } = this.state;

    const errors = get(updateError, 'response.data');
    const status = get(updateError, 'response.status');

    return errors && status !== 404 ? (
      <Alert className="MyAccountOrder__errors" color="danger" key={requestId}>
        {Object.values(updateError.response.data).map((message) => (
          <div key={message}>{message}</div>
        ))}
      </Alert>
    ) : (
      <UnexpectedError />
    );
  };

  renderMessage = () => {
    const { recurringOrderStore } = this.props;
    let message = null;

    switch (recurringOrderStore.orderStates.get(this.getCurrentOrderId())) {
      case RequestState.ERROR:
        message = (
          <Alert color="danger">
            <FormattedMessage
              id="order.orderNotFoundSentence"
              defaultMessage="Order is not found"
            />
          </Alert>
        );
        break;
      case RequestState.NONE:
      case RequestState.LOADING:
      case RequestState.LOADED:
      default:
        break;
    }

    return message;
  };

  render() {
    const { uiStore, recurringOrderStore } = this.props;
    const { updateError } = this.state;
    const orderId = this.getCurrentOrderId();
    const queryStatus = recurringOrderStore.orderStates.get(
      this.getCurrentOrderId()
    );

    return (
      <Fragment>
        <HelperNavigation breadcrumbsPath={this.getBreadcrumbsPath()} />
        <SEOTitle
          title={{
            ...messages['myAccount.orderTitle'],
            values: { orderId }
          }}
        />
        {queryStatus && this.renderMessage()}
        <ContentForState
          state={this.getOrderState()}
          forLoaded={() => {
            const order = this.getOrder();
            return (
              <div className="MyAccountRecurringOrder">
                <PageTitle className="MyAccountOrder__page-title">
                  <FormattedMessage
                    {...messages['myAccount.orderTitle']}
                    values={{ orderId }}
                  />{' '}
                  <span className="MyAccountOrder__status">
                    <OrderStatusLabel status={order.recurring_status} />
                  </span>
                </PageTitle>
                <Container className="MyAccountOrder__container">
                  <Row>
                    <Col>
                      <Card>
                        <CardHeader>
                          <FormattedMessage
                            {...globalTranslations.orderInfoHeader}
                          />
                          <div>
                            <FormattedMessage
                              {...globalTranslations.dateUpdatedTitle}
                            />{' '}
                            {DateTime.fromISO(order.updated_at).toLocaleString(
                              DateTime.DATETIME_MED_WITH_SECONDS
                            )}
                          </div>
                        </CardHeader>
                        <CardBody>
                          <Container className="MyAccountOrder__information-container">
                            <Row>
                              <Col>
                                {order && (
                                  <MainRecurringOrder
                                    onSubmit={this.onOrderUpdate}
                                    recurringOrder={order}
                                  />
                                )}
                                {updateError && this.renderError()}
                              </Col>
                            </Row>
                          </Container>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Card>
                        <CardHeader>
                          <FormattedMessage
                            {...globalTranslations.productsTitle}
                          />
                        </CardHeader>
                        <CardBody>
                          <Container className="MyAccountOrder__products-container">
                            <div className="MyAccountOrder__products">
                              {!uiStore.isMobile &&
                                this.renderProductHeaderRow()}
                              {order.products.map((product) =>
                                this.renderProductRow(product)
                              )}
                            </div>
                            <Row className="MyAccountOrder__products-totals-row">
                              <Col md="6" xl="8" />
                              <Col>{this.renderTotals(productTotals)}</Col>
                            </Row>
                          </Container>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </Container>
              </div>
            );
          }}
        />
      </Fragment>
    );
  }
}

MyAccountRecurringOrder.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
  recurringOrderStore: modelOf(RecurringOrderStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  history: RouterPropTypes.history.isRequired,
  intl: intlShape.isRequired,
  match: RouterPropTypes.match.isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired
};

export default injectIntl(
  withRouter(
    inject(
      'accountStore',
      'routeService',
      'countryStore',
      'recurringOrderStore',
      'uiStore',
      'configStore'
    )(MyAccountRecurringOrder)
  )
);



// WEBPACK FOOTER //
// ./src/components/my-account/MyAccountRecurringOrder/index.jsx